import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useYapDay } from '../contexts/YapDayContext';
import { Plus } from 'lucide-react';
import { motion } from 'motion/react';
import { useTheme } from './ThemeProvider';
import { useAuthGate } from '../contexts/AuthGateContext';


interface YapDayTimerProps {
  visible: boolean;
}

export function YapDayTimer({ visible }: YapDayTimerProps) {
  const { timeUntilReset } = useYapDay();
  const [mounted, setMounted] = useState(false);
  const { theme } = useTheme();
  const { isAuthed } = useAuthGate();
  const [showUI, setShowUI] = useState(true);

  useEffect(() => {
    const handleHide = () => setShowUI(false);
    const handleShow = () => setShowUI(true);

    window.addEventListener('hideFloatingUI', handleHide);
    window.addEventListener('showFloatingUI', handleShow);

    return () => {
      window.removeEventListener('hideFloatingUI', handleHide);
      window.removeEventListener('showFloatingUI', handleShow);
    };
  }, []);

  useEffect(() => {
    setMounted(true);
    return () => setMounted(false);
  }, []);

  if (!mounted || !visible || !showUI) return null;

  // Helper to determine if it's "Golden Hour" (last hour)
  const isGoldenHour = (timeUntilReset || "").startsWith("00:");
  
  const handleCreateYapClick = () => {
    window.dispatchEvent(new CustomEvent('openCreateYapModal'));
  };

  // Final Production Render: Portal for visibility, Inline Styles for reliable positioning
  return createPortal(
    <>
    <div
      style={{
        position: 'fixed',
        bottom: '110px',
        right: '16px',
        zIndex: 40, // Below modals (which are z-50)
        isolation: 'isolate',
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        gap: '12px'
      }}
    >
      {/* Floating Create Yap Button — posting UI, so it's hidden for logged-out
          visitors for the same reason the inline composer is. */}
      {isAuthed && (
      <motion.button
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        onClick={handleCreateYapClick}
        className="w-11 h-11 aspect-square shrink-0 rounded-full backdrop-blur-md flex items-center justify-center pointer-events-auto"
        style={{ 
          background: theme === 'dark' 
            ? 'linear-gradient(to right, #3b82f6, #2563eb)' 
            : 'linear-gradient(to right, #F39C12, #E74C3C)',
          boxShadow: '0 8px 16px rgba(0, 0, 0, 0.5)'
        }}
      >
        <Plus className="w-5 h-5 text-white" />
      </motion.button>
      )}

      {/* Yap Day Timer — HIDDEN for now (feed moved to a rolling 21-day window).
          Kept in place (not deleted) so it can be brought back later; flip the
          `false &&` back to render it again. */}
      {false && (
      <div
        className="h-11 px-4 rounded-full backdrop-blur-md flex items-center justify-center pointer-events-auto text-white transition-all duration-500"
        style={{
          background: isGoldenHour
            ? 'linear-gradient(to right, #facc15, #d97706)' // yellow-400 -> amber-600
            : 'linear-gradient(to right, #3b82f6, #2563eb)', // blue-500 -> blue-600
          boxShadow: '0 8px 16px rgba(0, 0, 0, 0.5)' // Strong drop shadow
        }}
      >
        <div className="flex flex-col items-center justify-center leading-none">
           <span className="font-bold uppercase opacity-90 tracking-wide mb-px" style={{ fontSize: '8.5px' }}>
             {isGoldenHour ? 'GOLDEN HOUR' : 'YAP DAY ENDS IN'}
           </span>
           <span className="font-bold font-mono tracking-wider leading-none" style={{ fontSize: '16px' }}>
             {timeUntilReset}
           </span>
        </div>
      </div>
      )}
    </div>
    </>,
    document.body
  );
}
